import { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { format } from "date-fns";
import { 
    useTheme, 
    useMediaQuery, 
    Box, 
    Typography, 
    CircularProgress,
    FormControl, 
    MenuItem, 
    InputLabel, 
    Select,       
} from '@mui/material';
import useAuthCheck from 'hooks/useAuthCheck';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import { 
    LAYOUT_HEIGHT, 
    DIVERT_RETURN_STATUS, 
    EXCLUDED_FLIGHT_STATUS 
} from 'state/constants';
import { removeItemByValueImmutable, filterFlights } from 'state/helpers';
import { countValuesByField, 
    countBlockTimeStackedBarChart, 
    countAvgMinutesByTableau 
} from './utils';
import Header from 'components/Header';
import FilterButton from 'components/FilterButton';
import ScrollToTop from 'components/ScrollToTop';
import FlexBetween from 'components/FlexBetween';
import ChartDialog from 'components/chartNivo/ChartDialog';
import FlightFilterDialog from 'components/FlightFilterDialog';
import SortBarMultiLinesChart from 'components/chartNivo/SortBarMultiLinesChart';
import SortStackedBarChart from 'components/chartNivo/SortStackedBarChart';
import CommonTableau from './CommonTableau';
import { boxStyles as styles } from 'components/CustomStyles';


const displayOptions = [
    { value: "chart", label: "Chart" },
    { value: "tableau", label: "Tableau" },
];

const viewOptions = {
    chart: [
        { value: "blockMinutesByFlightDate", label: "by Date" },
        { value: "blockMinutesByDep", label: "by Port" },
        { value: "blockMinutesBySector", label: "by Sector" },
        { value: "blockMinutesBySectorType", label: "by Sector Type" },
        { value: "blockMinutesByFlightType", label: "by Flight Type" },
        { value: "blockMinutesByFlightReg", label: "by Flight Reg" },
        { value: "blockMinutesByACType", label: "by AC Type" },
        { value: "blockMinutesByCountry", label: "by Country" },
        { value: "blockMinutesByFlightNo", label: "by Flight No" },
    ],
    tableau: [
        { value: "byDate", label: "by Date" },
        { value: "byDateDep", label: "by Date & Port" },
        { value: "byDateSector", label: "by Date & Sector" },
        { value: "byDateSectorType", label: "by Date & Sector Type" },
        { value: "byDateFlightType", label: "by Date & Flight Type" },
        { value: "byDateFlightReg", label: "by Date & Flight Reg" },
        { value: "byDateACType", label: "by Date & AC Type" },
        { value: "byDateCountry", label: "by Date & Country" },
        { value: "byDateFlightNo", label: "by Date & Flight No" },

        { value: "byDep", label: "by Port" },
        { value: "bySector", label: "by Sector" },
        { value: "bySectorType", label: "by Sector Type" },
        { value: "byFlightType", label: "by Flight Type" },

        { value: "byFlightReg", label: "by Flight Reg" },
        { value: "byFlightRegDep", label: "by Reg & Port" },
        { value: "byFlightRegSector", label: "by Reg & Sector" },
        { value: "byFlightRegSectorType", label: "by Reg & Sector Type" },
        { value: "byFlightRegFlightType", label: "by Reg & Flight Type" },
        { value: "byFlightRegCountry", label: "by Reg & Country" },

        { value: "byACType", label: "by AC Type" },
        { value: "byACTypeDep", label: "by AC & Port" },
        { value: "byACTypeSector", label: "by AC & Sector" },
        { value: "byACTypeSectorType", label: "by AC & Sector Type" },
        { value: "byACTypeFlightType", label: "by AC & Flight Type" },
        { value: "byACTypeCountry", label: "by AC & Country" },

        { value: "byCountry", label: "by Country" },
        { value: "byFlightNo", label: "by Flight No" },
    ],
};

const BlockMinutesPer1FltChart = () => {    
    useAuthCheck();
    const timeMode = useSelector((state) => state.timemode);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
        
    const { startDate, endDate } = useDateRangeContext();

    const scrollRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
    const [filters, setFilters] = useState(null);
    const [chartOpen, setChartOpen] = useState(false); 
    const [selectedChart, setSelectedChart] = useState(null);
    
    const [type, setType] = useState('actual');
    const [displayType, setDisplayType] = useState('chart');
    const [view, setView] = useState('blockMinutesByFlightDate');
    const [flights, setFlights] = useState([]); 
    const flightsData = useSelector((state) => state.flights); 

    useEffect(() => {        
        const handleFlights = async () => {
            try {           
                setLoading(true); 
                const processedFlights = removeItemByValueImmutable(flightsData, 'flightStatus', EXCLUDED_FLIGHT_STATUS)
                    .filter(flight => 
                        !DIVERT_RETURN_STATUS.includes(flight.flightStatus)
                    );
                setFlights(processedFlights);                
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        };        
        handleFlights();      
    }, [flightsData]);
        
    const filteredFlights = useMemo(() => {
        return filterFlights({ flights, filters, timeMode });
    }, [flights, filters, timeMode]);     
    
    const blockMinutes = useMemo(() => {
        switch (type) {
            case 'actual':  
                return 'actualBlockMinutes';
            case 'schedule':    
                return 'scheduleBlockMinutes';
            case 'estimated':   
                return 'estimatedBlockMinutes';
            default:    
                return 'blockMinutes';
        }   
    }, [type]); 
    
    const computedViewData = useMemo(() => {
        const f = filteredFlights;
        const flightDateField = timeMode === "UTC" ? "flightDate" : "flightDateLocalBase";

        switch (view) {
            case "blockMinutesByFlightDate":
                return {
                    column: countValuesByField(f, blockMinutes, flightDateField),
                    stacked: countBlockTimeStackedBarChart(f, flightDateField),
                };

            case "blockMinutesByDep":
                return {
                    column: countValuesByField(f, blockMinutes, "dep"),
                    stacked: countBlockTimeStackedBarChart(f, "dep"),
                };

            case "blockMinutesBySector":
                return {
                    column: countValuesByField(f, blockMinutes, "sector"),
                    stacked: countBlockTimeStackedBarChart(f, "sector"),
                };

            case "blockMinutesBySectorType":
                return {
                    column: countValuesByField(f, blockMinutes, "sectorType"),
                    stacked: countBlockTimeStackedBarChart(f, "sectorType"),
                };

            case "blockMinutesByFlightType":
                return {
                    column: countValuesByField(f, blockMinutes, "flightType"),
                    stacked: countBlockTimeStackedBarChart(f, "flightType"),
                };

            case "blockMinutesByFlightReg":
                return {
                    column: countValuesByField(f, blockMinutes, "flightReg"),
                    stacked: countBlockTimeStackedBarChart(f, "flightReg"),
                };

            case "blockMinutesByACType":
                return {
                    column: countValuesByField(f, blockMinutes, "acType"),
                    stacked: countBlockTimeStackedBarChart(f, "acType"),
                };

            case "blockMinutesByCountry":
                return {
                    column: countValuesByField(f, blockMinutes, "depCountryName"),
                    stacked: countBlockTimeStackedBarChart(f, "depCountryName"),
                };

            case "blockMinutesByFlightNo":
                return {
                    column: countValuesByField(f, blockMinutes, "flightNo"),
                    stacked: countBlockTimeStackedBarChart(f, "flightNo"),
                };

            default:
                return {};
        }
    }, [timeMode, view, filteredFlights, blockMinutes]);
    
    useEffect(() => {
        const options = viewOptions[displayType];
        if (!options?.length) return;
        const isValid = options.some(opt => opt.value === view);
        if (!isValid) setView(options[0].value);
    }, [displayType, view]);

    const safeView = useMemo(() => {
        const options = viewOptions[displayType] || [];
        return options.some(o => o.value === view)
            ? view
            : options[0]?.value || '';
    }, [view, displayType]);

    const tableauData = useMemo(() => {
        if (displayType !== 'tableau') return [];
        const flightDate = timeMode === "UTC" ? "flightDate" : "flightDateLocalBase";
        const f = filteredFlights || [];

        const fieldMap = {
            byDate:                [flightDate, null],
            byDateDep:             [flightDate, 'dep'],
            byDateSector:          [flightDate, 'sector'],
            byDateSectorType:      [flightDate, 'sectorType'],
            byDateFlightType:      [flightDate, 'flightType'],
            byDateFlightReg:       [flightDate, 'flightReg'],
            byDateACType:          [flightDate, 'acType'],
            byDateCountry:         [flightDate, 'depCountryName'],
            byDateFlightNo:        [flightDate, 'flightNo'],
            byDep:                 ['dep', null],
            bySector:              ['sector', null],
            bySectorType:          ['sectorType', null],
            byFlightType:          ['flightType', null],
            byFlightReg:           ['flightReg', null],
            byFlightRegDep:        ['flightReg', 'dep'],
            byFlightRegSector:     ['flightReg', 'sector'],
            byFlightRegSectorType: ['flightReg', 'sectorType'],
            byFlightRegFlightType: ['flightReg', 'flightType'],
            byFlightRegCountry:    ['flightReg', 'depCountryName'],
            byACType:              ['acType', null],
            byACTypeDep:           ['acType', 'dep'],
            byACTypeSector:        ['acType', 'sector'],
            byACTypeSectorType:    ['acType', 'sectorType'],
            byACTypeFlightType:    ['acType', 'flightType'],
            byACTypeCountry:       ['acType', 'depCountryName'],
            byCountry:             ['depCountryName', null],
            byFlightNo:            ['flightNo', null],
        };

        const fields = fieldMap[safeView];
        if (!fields) return [];
        return countAvgMinutesByTableau(f, fields[0], fields[1], blockMinutes);
    }, [displayType, safeView, timeMode, filteredFlights, blockMinutes]);

    const handleFilterOpen = () => setIsFilterDialogOpen(true);
    const handleFilterClose = () => setIsFilterDialogOpen(false);
    
    const handleFilterApply = (filterValues) => {
        setFilters(filterValues); 
    };
 
    const {
        acTypeOptions,
        flightRegOptions,
        sectorTypeOptions,
        departureTypeOptions,
        arrivalTypeOptions,
    } = useMemo(() => {
        if (!Array.isArray(flights)) {
            return {
                acTypeOptions: [],
                flightRegOptions: [],
                sectorTypeOptions: [],
                departureTypeOptions: [],
                arrivalTypeOptions: [],
            };
        }

        const sorted = (key) =>
            [...new Set(flights.map((f) => f[key]).filter(Boolean))].sort();

        return {
            acTypeOptions: sorted("acType"),
            flightRegOptions: sorted("flightReg"),
            sectorTypeOptions: sorted("sectorType"),
            departureTypeOptions: sorted("departureType"),
            arrivalTypeOptions: sorted("arrivalType"),
        };
    }, [flights]);

    const handleChartOpen = (chart) => {
        setSelectedChart(
            <Box sx={{ ...styles }}>
                <Box 
                    height="100%" 
                    width={"100%"} 
                    sx={chart.style}
                >
                    {chart.component}
                </Box>                
            </Box>
        );
        setChartOpen(true);
    };

    const handleChartClose = () => {
        setSelectedChart(null);
        setChartOpen(false);
    };

    const chartMap = useMemo(() => {
        const period = `${startDate ? format(startDate, 'ddMMMyy') : ''} - ${endDate ? format(endDate, 'ddMMMyy') : ''}`;
        const defaultTitle = `Block Time per 1 Flight ${period}`;
        
        return (
        {
            blockMinutesByFlightDate: [
                {
                    component: (
                        <SortBarMultiLinesChart
                            data={computedViewData.column}
                            title={defaultTitle}
                            bottomLegend="Date"
                            leftLegend="Minutes"
                            digit={1}
                            barTranslateXOffSet={30} 
                            lineTranslateXOffSet={-10}
                        />
                    ),
                },
                {
                    component: (
                        <SortStackedBarChart
                            data={computedViewData.stacked}
                            title={defaultTitle}
                            bottomLegend="Date"
                            leftLegend="Minutes"
                        />
                    ),
                },
            ],

            blockMinutesByDep: [
                {
                    component: (
                        <SortBarMultiLinesChart
                            data={computedViewData.column}
                            title={defaultTitle}
                            bottomLegend="Port"
                            leftLegend="Minutes"
                            digit={1}
                            barTranslateXOffSet={30} 
                            lineTranslateXOffSet={-10}
                        />
                    ),
                },
                {
                    component: (
                        <SortStackedBarChart
                            data={computedViewData.stacked}
                            title={defaultTitle}
                            bottomLegend="Port"
                            leftLegend="Minutes"
                        />
                    ),
                },
            ],

            blockMinutesBySector: [
                {
                    component: (
                        <SortBarMultiLinesChart
                            data={computedViewData.column}
                            title={defaultTitle}
                            bottomLegend="Sector"
                            leftLegend="Minutes"
                            digit={1}
                            bottomOffSet={10}
                            bottomLegendOffSet={10}
                            barTranslateXOffSet={30} 
                            lineTranslateXOffSet={-10}
                        />
                    ),
                },
                {
                    component: (
                        <SortStackedBarChart
                            data={computedViewData.stacked}
                            title={defaultTitle}
                            bottomLegend="Sector"
                            leftLegend="Minutes"
                            bottomOffSet={10}
                            bottomLegendOffSet={10}
                        />
                    ),
                },
            ],

            blockMinutesBySectorType: [
                {
                    component: (
                        <SortBarMultiLinesChart
                            data={computedViewData.column}
                            title={defaultTitle}
                            bottomLegend="Sector Type"
                            leftLegend="Minutes"
                            digit={1}
                            isHorizontalBottomLegend={true}
                            bottomOffSet={-30}
                            bottomLegendOffSet={-30}
                            barTranslateXOffSet={30} 
                            lineTranslateXOffSet={-10}
                        />
                    ),
                },
                {
                    component: (
                        <SortStackedBarChart
                            data={computedViewData.stacked}
                            title={defaultTitle}
                            bottomLegend="Sector Type"
                            leftLegend="Minutes"
                            isHorizontalBottomLegend={true}
                            bottomOffSet={-30}
                            bottomLegendOffSet={-30}
                        />
                    ),
                },
            ],

            blockMinutesByFlightType: [
                {
                    component: (
                        <SortBarMultiLinesChart
                            data={computedViewData.column}
                            title={defaultTitle}
                            bottomLegend="Flight Type"
                            leftLegend="Minutes"
                            digit={1}
                            isHorizontalBottomLegend={!isMobile ? true : undefined}
                            bottomOffSet={!isMobile ? -30 : 50}
                            bottomLegendOffSet={!isMobile ? -30 : 50}
                            barTranslateXOffSet={30} 
                            lineTranslateXOffSet={-10}
                        />
                    ),
                },
                {
                    component: (
                        <SortStackedBarChart
                            data={computedViewData.stacked}
                            title={defaultTitle}
                            bottomLegend="Flight Type"
                            leftLegend="Minutes"
                            isHorizontalBottomLegend={!isMobile ? true : undefined}
                            bottomOffSet={!isMobile ? -30 : 50}
                            bottomLegendOffSet={!isMobile ? -30 : 50}
                        />
                    ),
                },
            ],

            blockMinutesByFlightReg: [
                {
                    component: (
                        <SortBarMultiLinesChart
                            data={computedViewData.column}
                            title={defaultTitle}
                            bottomLegend="Flight Reg"
                            leftLegend="Minutes"
                            digit={1}
                            bottomOffSet={5}
                            bottomLegendOffSet={5}
                            barTranslateXOffSet={30} 
                            lineTranslateXOffSet={-10}
                        />
                    ),
                },
                {
                    component: (
                        <SortStackedBarChart
                            data={computedViewData.stacked}
                            title={defaultTitle}
                            bottomLegend="Flight Reg"
                            leftLegend="Minutes"
                            bottomOffSet={5}
                            bottomLegendOffSet={5}
                        />
                    ),
                },
            ],

            blockMinutesByACType: [
                {
                    component: (
                        <SortBarMultiLinesChart
                            data={computedViewData.column}
                            title={defaultTitle}
                            bottomLegend="AC Type"
                            leftLegend="Minutes"
                            digit={1}
                            barTranslateXOffSet={30} 
                            lineTranslateXOffSet={-10}
                        />
                    ),
                },
                {
                    component: (
                        <SortStackedBarChart
                            data={computedViewData.stacked}
                            title={defaultTitle}
                            bottomLegend="AC Type"
                            leftLegend="Minutes"
                        />
                    ),
                },
            ],

            blockMinutesByCountry: [
                {
                    component: (
                        <SortBarMultiLinesChart
                            data={computedViewData.column}
                            title={defaultTitle}
                            bottomLegend="Country"
                            leftLegend="Minutes"
                            digit={1}
                            bottomOffSet={45}
                            bottomLegendOffSet={45}
                            barTranslateXOffSet={30} 
                            lineTranslateXOffSet={-10}
                        />
                    ),
                },
                {
                    component: (
                        <SortStackedBarChart
                            data={computedViewData.stacked}
                            title={defaultTitle}
                            bottomLegend="Country"
                            leftLegend="Minutes"
                            bottomOffSet={45}
                            bottomLegendOffSet={45}
                        />
                    ),
                },
            ],

            blockMinutesByFlightNo: [
                {
                    component: (
                        <SortBarMultiLinesChart
                            data={computedViewData.column}
                            title={defaultTitle}
                            bottomLegend="Flight No"
                            leftLegend="Minutes"
                            digit={1}
                            bottomOffSet={5}
                            bottomLegendOffSet={5}
                            barTranslateXOffSet={30} 
                            lineTranslateXOffSet={-10}
                        />
                    ),
                },
                {
                    component: (
                        <SortStackedBarChart
                            data={computedViewData.stacked}
                            title={defaultTitle}
                            bottomLegend="Flight No"
                            leftLegend="Minutes"
                            bottomOffSet={5}
                            bottomLegendOffSet={5}
                        />
                    ),
                },
            ],
        }
    )}, [startDate, endDate, isMobile, computedViewData]);

    const renderCharts = (charts, customStyles) => (
        <Box
            ref={scrollRef}
            height={LAYOUT_HEIGHT}  
            display="flex"
            flexDirection="column"
            gap={2}
            sx={{ gridColumn: "span 12", overflowY: 'auto' }}
        >
            {charts.map((chart, index) => (
                <Box
                    key={index}
                    onDoubleClick={() => handleChartOpen(chart)}
                    sx={{ ...styles, ...customStyles }}
                >
                    <Box 
                        height="100%" 
                        width={"100%"} 
                        sx={chart.style}
                    >
                        {chart.component}
                    </Box>
                </Box>
            ))}
        </Box>
    );

    
    return (
        <Box className="main-container-box">                  
            <FlexBetween>
                <Header
                    title="Block Time"
                    subtitle={loading ? 'Loading...' : displayType === 'chart' ? 'List of Charts' : 'List of Tableaus'}
                />     
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        {!isMobile && (
                            <FormControl 
                                size="small" 
                                sx={{ width: isMobile ? '6rem' : '9rem' }}
                            >
                                <InputLabel>Type</InputLabel>
                                <Select
                                    label="Type"
                                    value={type}
                                    onChange={(e) => setType(e.target.value)}
                                >
                                    <MenuItem value="actual">Actual</MenuItem>
                                    <MenuItem value="schedule">Schedule</MenuItem>
                                    <MenuItem value="estimated">Estimated</MenuItem>
                                </Select>
                            </FormControl>
                        )}

                        <FormControl 
                            size="small" 
                            sx={{ width: isMobile ? "6rem" : "8rem" }}
                        >
                            <InputLabel>Display</InputLabel>
                            <Select
                                value={displayType}
                                label="Display"
                                onChange={(e) => setDisplayType(e.target.value)}
                            >
                                {displayOptions.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl
                            size="small"
                            sx={{ width: isMobile ? '7rem' : '12rem' }}
                        >
                            <InputLabel id="block-minutes-view-label">View</InputLabel>
                            <Select
                                labelId="block-minutes-view-label"
                                label="View"
                                value={safeView}
                                onChange={(e) => setView(e.target.value)}
                            >
                                {viewOptions[displayType].map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FilterButton
                            filters={filters}
                            onClick={handleFilterOpen}
                        />
                    </Box>
                )}
            </FlexBetween>             
            {loading ? (
                <Box sx={{ ...styles, height: LAYOUT_HEIGHT }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Box sx={{ ...styles, height: LAYOUT_HEIGHT }}>
                    <Typography color="error">
                        Error: {error?.message || ''}
                    </Typography>
                </Box>
            ) : (
                <Box
                    display="grid"                        
                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"                  
                >
                    {displayType === 'chart' && safeView in chartMap &&
                        renderCharts(chartMap[safeView], { minHeight: LAYOUT_HEIGHT })
                    }

                    {displayType === 'tableau' &&
                        renderCharts([{ component: <CommonTableau data={tableauData} />, style: {} }], { minHeight: LAYOUT_HEIGHT })
                    }

                    <ScrollToTop scrollRef={scrollRef} />
                </Box>
            )}

            {isFilterDialogOpen &&
                <FlightFilterDialog
                    open={isFilterDialogOpen}
                    onClose={handleFilterClose}
                    onApply={handleFilterApply}
                    acTypeOptions={acTypeOptions} 
                    flightRegOptions={flightRegOptions}
                    sectorTypeOptions={sectorTypeOptions}
                    departureTypeOptions={departureTypeOptions}
                    arrivalTypeOptions={arrivalTypeOptions}
                    currentFilters={filters}
                />
            }

            {chartOpen && (
                <ChartDialog
                    open={chartOpen}
                    onClose={handleChartClose}
                    chartComponent={selectedChart}
                />
            )}
        </Box>
    );
};

export default BlockMinutesPer1FltChart;
